import {AxoisRequestManager} from "../../stores/managers/RequestManager";
import {from} from "rxjs";
import type SitApi from "../SitApit";
import type {PageResponse} from "~/services/ApiTypes.ts";
import {makeAutoObservable} from "mobx";
import type {Category, Company, ComplexityTier, DOI, FirstPageKpis, Initiative} from "../ApiTypes";
import {DOIs} from "../ApiTypes";
import {inOrNotIn} from "./sitFilterParams";
import type AuthStore from "../../authentication/AuthStore";

export type InitiativeFilterValues = Pick<InitiativeListStore,
    'my_initiatives' | 'include_deactivated' | 'categories' | 'companies' | 'dois' | 'complexity_tiers' | 'yearsByDoi' | 'excluded'>;

export class InitiativeListStore {
    // filters
    search: string = '';
    my_initiatives: number = 0;
    /** Deactivated initiatives are left out of the list and its export unless this is on. */
    include_deactivated = false;
    categories: Category[] = [];
    companies: Company[] = [];
    dois: DOI[] = [];
    complexity_tiers: ComplexityTier[] = [];
    // Selected years per DOI stage gate (see cat-material's InitiativeQS.annotate_doi_years),
    // sent as one `year_doi_{stage}__in` filter per non-empty stage.
    yearsByDoi: Record<DOI, number[]> = {1: [], 2: [], 3: [], 4: [], 5: []};
    /** Filters in "Excluding" mode: 'companies', 'categories', 'dois', 'complexity_tiers', 'year_<doi>'. */
    excluded: string[] = [];
    per_page = 10;
    page = 0;
    ordering = '-total_saving';
    approval_per_page = 10;
    approval_page = 0;
    // Debounce handle for the search box.
    private searchTimer?: ReturnType<typeof setTimeout>;
    // Request managers
    readonly initiativeListRM = new AxoisRequestManager<{filters: string[][]}, PageResponse<Initiative>>(
        ({filters}) => from(this.api.listInitiatives(filters)));
    readonly initiativeKpiRM = new AxoisRequestManager<{filters: string[][]}, FirstPageKpis>(
        ({filters}) => from(this.api.firstPageKpis(filters)));
    readonly initiativeApprovalListRM = new AxoisRequestManager<{filters: string[][]}, PageResponse<Initiative>>(
        ({filters}) => from(this.api.listInitiativesApproval(filters)));

    constructor(private api: SitApi, private authStore: AuthStore) {
        makeAutoObservable(this, {});
    }

    reloadData(onlyIfEmpty = false, resetPage = true) {
        if (onlyIfEmpty && (this.initiativeKpiRM.result !== undefined || this.initiativeKpiRM.busy))
            return;
        if (resetPage)
            this.page = 0;
        const filters: [string, string][] = [];
        if (this.search.trim().length > 0) filters.push(['search', this.search.trim()]);
        if (this.my_initiatives && this.authStore.user?.sit_user_profile?.id) filters.push(['owner', String(this.authStore.user.sit_user_profile.id)]);
        if (this.include_deactivated) filters.push(['include_deactivated', 'true']);
        if (this.companies.length > 0) filters.push([inOrNotIn('financial__companies__id', this.excluded.includes('companies')), String(this.companies.map(c => c.id))])
        if (this.categories.length > 0) filters.push([inOrNotIn('financial__categories__id', this.excluded.includes('categories')), String(this.categories.map(c => c.id))])
        if (this.dois.length > 0) filters.push([inOrNotIn('doi_status', this.excluded.includes('dois')), String(this.dois)]);
        if (this.complexity_tiers.length > 0) filters.push([inOrNotIn('complexity_tier', this.excluded.includes('complexity_tiers')), String(this.complexity_tiers)]);
        DOIs.forEach(doi => {
            if (this.yearsByDoi[doi].length > 0) filters.push([inOrNotIn(`year_doi_${doi}`, this.excluded.includes(`year_${doi}`)), String(this.yearsByDoi[doi])]);
        });
        this.initiativeKpiRM.request({filters});
        filters.push(['page_size', String(this.per_page)])
        if (this.page > 0) filters.push(['page', String(this.page + 1)]);
        if (this.ordering !== '') filters.push(['ordering', this.ordering]);
        this.initiativeListRM.request({filters});
    }

    /** The active filters/search/ordering (no pagination) for the Excel export. */
    get exportFilters(): [string, string][] {
        const filters: [string, string][] = [];
        if (this.search.trim().length > 0) filters.push(['search', this.search.trim()]);
        if (this.my_initiatives && this.authStore.user?.sit_user_profile?.id) filters.push(['owner', String(this.authStore.user.sit_user_profile.id)]);
        if (this.include_deactivated) filters.push(['include_deactivated', 'true']);
        if (this.companies.length > 0) filters.push([inOrNotIn('financial__companies__id', this.excluded.includes('companies')), String(this.companies.map(c => c.id))]);
        if (this.categories.length > 0) filters.push([inOrNotIn('financial__categories__id', this.excluded.includes('categories')), String(this.categories.map(c => c.id))]);
        if (this.dois.length > 0) filters.push([inOrNotIn('doi_status', this.excluded.includes('dois')), String(this.dois)]);
        if (this.complexity_tiers.length > 0) filters.push([inOrNotIn('complexity_tier', this.excluded.includes('complexity_tiers')), String(this.complexity_tiers)]);
        DOIs.forEach(doi => {
            if (this.yearsByDoi[doi].length > 0) filters.push([inOrNotIn(`year_doi_${doi}`, this.excluded.includes(`year_${doi}`)), String(this.yearsByDoi[doi])]);
        });
        if (this.ordering !== '') filters.push(['ordering', this.ordering]);
        return filters;
    }

    exportInitiatives() {
        return this.api.exportInitiatives(this.exportFilters);
    }

    reloadApprovalData(onlyIfEmpty = false) {
        if (onlyIfEmpty && (this.initiativeApprovalListRM.result !== undefined || this.initiativeApprovalListRM.busy))
            return;
        const filters: [string, string][] = [];
        filters.push(['page_size', String(this.approval_per_page)])
        if (this.approval_page > 0) filters.push(['page', String(this.approval_page + 1)]);
        this.initiativeApprovalListRM.request({filters});
    }

    setSearch(value: string) {
        // Update the field immediately (responsive input), debounce the reload.
        this.search = value;
        if (this.searchTimer) clearTimeout(this.searchTimer);
        this.searchTimer = setTimeout(() => this.reloadData(), 350);
    }

    /** Set several filters at once (the filter bar's Apply) and reload once. */
    applyFilters(f: InitiativeFilterValues) {
        this.my_initiatives = f.my_initiatives;
        this.include_deactivated = f.include_deactivated;
        this.categories = f.categories;
        this.companies = f.companies;
        this.dois = f.dois;
        this.complexity_tiers = f.complexity_tiers;
        this.yearsByDoi = f.yearsByDoi;
        this.excluded = f.excluded;
        this.reloadData();
    }

    setMyInitiatives(value: number) {
        this.my_initiatives = value;
        this.reloadData();
    }

    setCategories(value: Category[]) {
        this.categories = value;
        this.reloadData();
    }

    setCompanies(value: Company[]) {
        this.companies = value;
        this.reloadData();
    }

    setDois(value: DOI[]) {
        this.dois = value;
        this.reloadData();
    }

    setComplexityTiers(value: ComplexityTier[]) {
        this.complexity_tiers = value;
        this.reloadData();
    }

    setYearsForDoi(doi: DOI, value: number[]) {
        this.yearsByDoi[doi] = value;
        this.reloadData();
    }

    setPerPage(value: number) {
        if (value > 0) {
            this.per_page = value;
            this.reloadData();
        }
    }

    setPage(value: number) {
        if (value >= 0) {
            this.page = value;
            this.reloadData(false, false);
        }
    }

    setOrdering(value: string) {
        this.ordering = value;
        this.reloadData();
    }

    setApprovalPerPage(value: number) {
        if (value > 0) {
            this.approval_per_page = value;
            this.approval_page = 0;
            this.reloadApprovalData();
        }
    }

    setApprovalPage(value: number) {
        if (value >= 0) {
            this.approval_page = value;
            this.reloadApprovalData();
        }
    }
}